import { useCallback, useEffect, useRef } from 'react';

const sameMessage = (left, right) => left.id === right.id
    && left.flowSessionId === right.flowSessionId
    && String(left.response || '').trim() === String(right.response || '').trim()
    && JSON.stringify(left.options || left.buttons || []) === JSON.stringify(right.options || right.buttons || []);

/** Resolve acceptance from committed props, never from a state updater. */
export function useWebhookMessageAcceptance({ messages, setMessages, getConversationId }) {
    const pending = useRef(new Set());
    const committedMessages = useRef([]);
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        const acceptances = pending.current;
        return () => {
            mounted.current = false;
            for (const acceptance of acceptances) acceptance.resolve(false);
            acceptances.clear();
        };
    }, []);
    useEffect(() => {
        // The legacy conversation ID is mutable global state, not a React prop.
        const timer = setInterval(() => {
            for (const acceptance of pending.current) {
                if (acceptance.message.flowSessionId !== getConversationId()) {
                    pending.current.delete(acceptance);
                    acceptance.resolve(false);
                }
            }
        }, 100);
        return () => clearInterval(timer);
    }, [getConversationId]);
    useEffect(() => {
        committedMessages.current = messages;
        for (const acceptance of pending.current) {
            const stale = acceptance.message.flowSessionId !== getConversationId();
            if (stale || messages.some(message => sameMessage(message, acceptance.message))) {
                pending.current.delete(acceptance);
                acceptance.resolve(!stale);
            }
        }
    });
    return useCallback(message => new Promise(resolve => {
        if (!mounted.current || !message.flowSessionId || message.flowSessionId !== getConversationId()) { resolve(false); return; }
        if (committedMessages.current.some(item => sameMessage(item, message))) { resolve(true); return; }
        pending.current.add({ message, resolve });
        setMessages(previous => {
            if (!mounted.current || message.flowSessionId !== getConversationId()) return previous;
            if (previous.some(item => sameMessage(item, message))) return previous;
            const cleanPrevious = previous.filter(item => !item.typing && !(item.origin === 'bot' && item.response === 'Digitando...'));
            return [...cleanPrevious, message];
        });
    }), [setMessages, getConversationId]);
}
